import {useEffect, useRef, useState} from 'react'
import {caronaApi} from '../../../services/CaronaApi.service'
import {BuscaRecente, JustificativaResumo} from '../types'
import JustificativasList from './JustificativasList'

// Quantos casos exibir direto na landing (os mais recentes; o resto fica em
// "Minhas Pesquisas"). O backend já devolve org-scoped e recentes primeiro.
const CASOS_NA_LANDING = 6

export interface BuscaFormValues {
    objeto: string
    esferaGerenciador: string
    uf: string
    vigentes: boolean
}

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT',
    'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR',
    'SC', 'SP', 'SE', 'TO']

// Typeahead (autocomplete do campo a partir do PNCP) — IMPLEMENTADO, porém DESATIVADO
// por padrão. Motivo: o PNCP só expõe a DESCRIÇÃO INTEIRA da ata (textos longos), que
// poluem o buscador de atas (aqui o ideal é um termo curto). A função casaria melhor
// num formulário de GERAÇÃO DE DOCUMENTO (onde colar uma descrição completa ajuda).
// O endpoint backend (GET /v1/atas/autocomplete) e a UI ficam prontos para reuso.
// Para reativar: mude para `true`.
const TYPEAHEAD_ATIVO = false

interface Props {
    onBuscar: (form: BuscaFormValues) => void
    onHistorico: () => void
    /** Abrir uma pesquisa concluída direto da landing (mesmo handler da HistoricoView). */
    onAbrirCaso: (id: string) => void
    /** Retomar um rascunho direto da landing (mesmo handler da HistoricoView). */
    onRetomarCaso: (id: string) => void
}

export default function BuscaForm({onBuscar, onHistorico, onAbrirCaso, onRetomarCaso}: Props) {
    const [objeto, setObjeto] = useState('')
    const [esferaGerenciador, setEsferaGerenciador] = useState('')
    const [uf, setUf] = useState('')
    const [vigentes, setVigentes] = useState(true)
    const [erroObjeto, setErroObjeto] = useState<string | null>(null)
    const objetoVazio = objeto.trim() === ''

    // Typeahead: sugestões de objeto a partir do PNCP (cache + ao vivo), com debounce.
    const [auto, setAuto] = useState<string[]>([])
    const [mostrarAuto, setMostrarAuto] = useState(false)
    const selecionou = useRef(false)

    // "Suas últimas buscas" (home da busca): carrega 1x ao montar. Best-effort —
    // vazio ou erro simplesmente não mostra o bloco (sem estado de erro na tela).
    const [recentes, setRecentes] = useState<BuscaRecente[]>([])
    useEffect(() => {
        caronaApi.buscasRecentes()
            .then((r) => setRecentes(r || []))
            .catch(() => setRecentes([]))
    }, [])

    // Lista de casos (rascunhos + concluídas) NA LANDING — padrão da Pesquisa de
    // Preços: o usuário clica direto para abrir/retomar, sem entrar num menu à parte.
    // Recarrega a cada montagem (a landing remonta ao voltar da entrevista/geração),
    // então reflete casos novos sem coordenação extra. Best-effort (falha = vazio).
    const [casos, setCasos] = useState<JustificativaResumo[]>([])
    const [carregandoCasos, setCarregandoCasos] = useState(true)
    useEffect(() => {
        let vivo = true
        caronaApi.listarJustificativas()
            .then((cs) => { if (vivo) setCasos(Array.isArray(cs) ? cs : []) })
            .catch(() => { if (vivo) setCasos([]) })
            .finally(() => { if (vivo) setCarregandoCasos(false) })
        return () => { vivo = false }
    }, [])

    useEffect(() => {
        if (!TYPEAHEAD_ATIVO) return // desativado por padrão (ver comentário no topo)
        if (selecionou.current) { selecionou.current = false; return } // não busca após escolher
        const q = objeto.trim()
        if (q.length < 3) { setAuto([]); setMostrarAuto(false); return }
        const t = setTimeout(() => {
            caronaApi.autocomplete(q)
                .then((s) => { setAuto(s); setMostrarAuto(s.length > 0) })
                .catch(() => { setAuto([]); setMostrarAuto(false) })
        }, 350)
        return () => clearTimeout(t)
    }, [objeto])

    function escolherSugestao(s: string) {
        selecionou.current = true
        setObjeto(s)
        setErroObjeto(null)
        setMostrarAuto(false)
    }

    function submeter() {
        setMostrarAuto(false)
        if (objeto.trim() === '') {
            setErroObjeto('Informe o objeto ou item desejado.')
            return
        }
        onBuscar({objeto: objeto.trim(), esferaGerenciador, uf, vigentes})
    }

    // Chip de busca recente: preenche o campo e já dispara a busca (com os demais
    // filtros como estão na tela).
    function buscarRecente(termo: string) {
        setObjeto(termo)
        setErroObjeto(null)
        setMostrarAuto(false)
        onBuscar({objeto: termo.trim(), esferaGerenciador, uf, vigentes})
    }

    return (
        <div>
            <header className="page-header">
                <div className="header-row">
                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        <span className="header-id">
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>
                        </span>
                        <h1 className="lg-h2">Ata Inteligente</h1>
                        <span className="lg-badge lg-badge--brand" style={{alignSelf: 'center'}}>Novo</span>
                    </div>
                    <button className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onHistorico}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        Minhas Pesquisas
                    </button>
                </div>
                <p className="lg-text-muted" style={{marginBottom: 0}}>
                    Pesquisa de Atas de Registro de Preços vigentes (PNCP) para <strong>aderir (carona)</strong> e de
                    Intenções de Registro de Preços (<strong>IRP</strong>) federais abertas, para <strong>participar antes da licitação</strong>.
                    A compatibilidade de esfera do seu órgão (art. 86, §8º) é confirmada na entrevista. Base legal: Lei 14.133/2021, arts. 82–86.
                </p>
            </header>

            <div className="page-content">
                <div className="section-divider">
                    <span className="section-divider__label">O que você precisa contratar</span>
                    <div className="section-divider__line"></div>
                </div>

                <div style={{display: 'flex', flexDirection: 'column', gap: 18}}>
                    <div className="lg-field" style={{position: 'relative'}}>
                        <label className="lg-label lg-label--required" htmlFor="busca-objeto">Objeto / item desejado</label>
                        <input id="busca-objeto" className="lg-input" type="text" value={objeto} autoComplete="off"
                            aria-required="true"
                            aria-invalid={erroObjeto ? true : undefined}
                            aria-describedby={erroObjeto ? 'busca-objeto-erro' : undefined}
                            onChange={(e) => { setObjeto(e.target.value); setErroObjeto(null) }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submeter()
                                else if (e.key === 'Escape') setMostrarAuto(false)
                            }}
                            onFocus={() => auto.length > 0 && setMostrarAuto(true)}
                            onBlur={() => setTimeout(() => setMostrarAuto(false), 150)}
                            placeholder="Ex.: caneta esferográfica, notebook, medicamento, ar condicionado" />
                        {erroObjeto && (
                            <span id="busca-objeto-erro" role="alert" className="lg-field__error">{erroObjeto}</span>
                        )}
                        {mostrarAuto && auto.length > 0 && (
                            <ul className="auto-dropdown">
                                {auto.map((s, i) => (
                                    <li key={i} className="auto-item"
                                        onMouseDown={(e) => { e.preventDefault(); escolherSugestao(s) }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                                        <span>{s.length > 100 ? s.slice(0, 100) + '…' : s}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {recentes.length > 0 && (
                        <div className="lg-field">
                            <label className="lg-label">Suas últimas buscas</label>
                            <div className="sugestoes__chips" style={{justifyContent: 'flex-start', maxWidth: 'none'}}>
                                {recentes.map((b) => (
                                    <button key={b.termo} className="sugestao-chip" title={`Pesquisar novamente: ${b.termo}`}
                                        onClick={() => buscarRecente(b.termo)}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                        {b.termo.length > 60 ? b.termo.slice(0, 60) + '…' : b.termo}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-row form-row--4">
                        <div className="lg-field">
                            <label className="lg-label">Código CATMAT / CATSER</label>
                            <input className="lg-input" type="text" placeholder="opcional — ex.: 150018" />
                        </div>
                        <div className="lg-field">
                            <label className="lg-label">Esfera do gerenciador</label>
                            <select className="lg-select" value={esferaGerenciador}
                                onChange={(e) => setEsferaGerenciador(e.target.value)}>
                                <option value="">Todas</option>
                                <option value="F">Federal</option>
                                <option value="E">Estadual</option>
                                <option value="M">Municipal</option>
                                <option value="D">Distrital</option>
                            </select>
                        </div>
                        <div className="lg-field">
                            <label className="lg-label">UF</label>
                            <select className="lg-select" value={uf} onChange={(e) => setUf(e.target.value)}>
                                <option value="">Todas</option>
                                {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                        <div className="lg-field">
                            <label className="lg-label">Valor unit. até (R$)</label>
                            <input className="lg-input" type="text" placeholder="ex.: 6.000" />
                        </div>
                    </div>

                    <div className="toggle-row">
                        <div className={`toggle${vigentes ? ' is-on' : ''}`} onClick={() => setVigentes((v) => !v)}></div>
                        <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>
                            Mostrar apenas atas <strong>vigentes</strong> (recomendado — a adesão só pode ocorrer com ata vigente, art. 84)
                        </span>
                    </div>

                    <div className="callout callout--info">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                        <div>A busca traz <strong>todas as atas vigentes</strong> que podem permitir adesão — com selo <strong>verde</strong> (permitida) ou <strong>amarelo</strong> (a confirmar), consultando o <strong>PNCP em tempo real</strong>. A compatibilidade de esfera e a cláusula de adesão são confirmadas na entrevista.</div>
                    </div>

                    <div className="actions-bar" style={{borderTop: 'none', justifyContent: 'flex-end', paddingTop: 0}}>
                        <button className="lg-btn lg-btn--brand lg-btn--lg" onClick={submeter} disabled={objetoVazio}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                            Pesquisar atas
                        </button>
                    </div>
                </div>

                <div className="section-divider" style={{marginTop: 26}}>
                    <span className="section-divider__label">Minhas Pesquisas</span>
                    <div className="section-divider__line"></div>
                </div>
                <JustificativasList
                    justificativas={casos.slice(0, CASOS_NA_LANDING)} loading={carregandoCasos}
                    onAbrir={onAbrirCaso} onRetomar={onRetomarCaso}
                    vazioTitulo="Você ainda não tem pesquisas"
                    vazioDescricao="Pesquise uma ata acima, avalie a adesão e gere a primeira — seus rascunhos e concluídas aparecem aqui para abrir ou retomar."
                />
                {casos.length > CASOS_NA_LANDING && (
                    <div style={{display: 'flex', justifyContent: 'center', marginTop: 12}}>
                        <button className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onHistorico}>
                            Ver todas ({casos.length})
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
